/* eslint-disable no-unused-vars */
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  CancelToken,
  Method,
} from "axios";
import type { Key } from "swr";
import { IExploreInstructorInput } from "./explore";
import { STATUS, WEBINAR_STATUS } from "types/common";
import { COURSE_TYPE, FESTIVAL_KIND } from "./course";
import { CREATE_CERTIFICATE_MODE } from "enums/certificate";
import { ApiProductCardImages } from "./product-image";

export type Header = Record<string, string>;

export interface GateRequest<D, P> {
  client: AxiosInstance;
  method: Method;
  url: string;
  data?: D;
  params?: P;
  isForm?: boolean;
  headers?: Header;
}

export type GateMethodRequest<D, P> = Omit<
  GateRequest<D, P>,
  "method" | "isForm"
>;

export interface RequestMeta<T, D, P> {
  request: AxiosRequestConfig;
  cancelToken: CancelToken;
  cancel: (message?: string | undefined) => void;
  fetch: () => Promise<T>;
  page: (req: Pick<GateRequest<D, P>, "data" | "params">) => Promise<T>;
  key: () => string;
}
export type ValidationError<T> = {
  members: (keyof T)[];
  message: string;
};
export type ErrorMeta<T = unknown> = {
  code: number;
  details: string;
  message: string;
  validationErrors?: ValidationError<T>[] | null;
};

export type ApiResponse<T = never> = {
  result: T;
  success: boolean;
  targetUrl: string | null;
};
export type ErrorResponse<T = never> = {
  error: ErrorMeta<T>;
  unAuthorizedRequest: boolean;
  __abp: boolean;
  canceled?: boolean;
  __internal?: boolean;
  faildRequest?: boolean;
  stack?: AxiosError<any>;
  url?: string;
};

type Args<R, D, P> = RequestMeta<R, D, P> | null | undefined | false;

type K<R, D, P> = Args<R, D, P> | (() => Args<R, D, P>);

export type ReqKey<R, D, P> = K<R, D, P> | [K<R, D, P>, ...Key[]] | [];
export interface ICartInput extends ApiProductCardImages {
  id: number;
  title: string;
  title_short?: string;
  sku: string;
  slug: string;
  status: STATUS;
  progress_status?: STATUS;
  price: number;
  duration: {
    hours: number;
    in_minutes: number;
    minutes: number;
    rounded_in_hour: number;
  };
  product_view?: number;
  is_buy: boolean;
  is_favorite: boolean;
  is_votive?: boolean;
  total_orders?: number;
  url?: string;
  instructors?: IExploreInstructorInput[];
  price_display?: string;
  webinar_status?: WEBINAR_STATUS;
  webinar_times?: {
    end_at: string;
    updated_at: string;
    product_id: number;
    created_at: string;
    id: number;
    start_at: string;
    url: string;
    capacity: number;
  }[];
  watchTime_Percent?: number;
  deadline?: string;
  watch_percent?: number;
  type: COURSE_TYPE;
  in_cart?: boolean;
  festival_new?: festival;
  attribute_type?: COURSE_TYPE;
  hasCertificate?: boolean;
  has_certificate?: boolean;
  details_expire?: DetailsExpire;
  student_count?: number;
  festival?: festival;
}

export interface DetailsExpire {
  name: string;
  coupon_expire_at: string;
  window_end_at: string;
  window_end_at_formatted: string;
  can_purchase: boolean;
  value_percent: number;
  expiry_status: EXPIRY_STATUS;
  can_purchase_after_coupon: boolean;
  product_timed_expire_formated: string;
  value_percent_after_coupon: number;
}
export interface IDiscountRemovedOutput {
  status: boolean;
}
export interface IDiscountItemOutput {
  status: boolean;
  coupon_code: string;
  original_amount: number;
  amount_without_balance: number;
  order_discount: number;
  coupon_amount: number;
  member_ship_amount: number;
  reason: string;
  message: string;
  successOrderCount: string;
  paid_order_count_status: string;
}

export type Pagination = {
  limit: number;
  load_more: boolean;
  page: number;
  total: number;
  total_question: number;
};

export interface PaginationData {
  page?: number;
  limit?: number;
}

export interface IDiscountItemInput {
  status: boolean;
  coupon_code: string;
  original_amount: number;
  amount_without_balance: number;
  order_discount: number;
  coupon_amount: number;
  member_ship_amount: number;
  reason: string;
  message: string;
  successOrderCount: string;
  paid_order_count_status: string;
}

export interface ICartCourseInput {
  id: number;
  cart_hash: string;
  title: string;
  image: string;
  price: number;
  sku: string;
  url: string;
}

export interface IAccessMenuInput {
  status: boolean;
  data: {
    page_access?: { course_detail?: boolean; access_page?: boolean };
  };
}

export interface IDirectPayment {
  status: boolean;
  hasContract: boolean;
  message: string;
}

export enum DISPLAY {
  LIST_LG = "list-lg",
  GRID = "grid",
}

export enum NOWRUZ_ACTION {
  START = "start",
  VIEW = "view",
  SAVE = "save",
  SHARE = "share",
  COPY = "copy",
  EXPLORE = "explore",
}

export type OLogExperiment =
  | homeExperiment
  | certificateExperiment
  | searchExperiment
  | NowruzReportExperiment
  | feedbackExperiment
  | FeedbackExperimentContact
  | subscriptionExperiment
  | ISubscriptionRecommenderExperiment
  | OrganizationFormExperiment;

interface logExperiment {
  tag: TAG_EXPERIMENT;
}

interface homeExperiment extends logExperiment {
  anyJoson: {
    where?: string;
    is_login: boolean;
    user_id?: number | null;
    age?: number | null;
    product_count?: number | null;
    product_count_free?: number | null;
    order?: number;
    version?: string;
  };
}

interface certificateExperiment extends logExperiment {
  anyJoson: {
    product_id?: Number;
    user_id: number;
    certificate_id?: Number;
    linkedin_url: string;
    type?: CERTIFICATE_TYPE_LOG | CREATE_CERTIFICATE_MODE;
  };
}

interface searchExperiment extends logExperiment {
  anyJoson: {
    user_id?: number | null;
    age?: number | null;
    product_count?: number | null;
    product_count_free?: number | null;
    term: string;
  };
}

export type SubscriptionRecommenderPositions =
  | "footer-banner"
  | "footer-links"
  | "header-btn"
  | "bottom-sticky-banner"
  | "how-to-learn-banner"
  | "product-banner"
  | "home-banner"
  | "programs"
  | "access-subscription-banner"
  | "videos-subscription-banner";

export interface ISubscriptionRecommenderExperiment extends logExperiment {
  tag: TAG_EXPERIMENT.SUBSCRPTION_RECOMMENDER;
  anyJson: {
    is_login: boolean;
    user_id: number | null;

    user_age_days: number | null;
    first_order_age_days: number | null;
    last_order_age_days: number | null;
    revenue_observation_days: number | null;
    last_year_revenue_irt: number | null;
    estimated_year_revenue_irt: number | null;
    average_similarity: number | null;
    max_similarity: number | null;
    final_score: number | null;

    page_url: string;
    position: SubscriptionRecommenderPositions;
    action: SUBS_ACTION_TYPE;
  };
}

interface NowruzReportExperiment extends logExperiment {
  anyJoson: {
    user_id: number | null;
    action: NOWRUZ_ACTION;
    linkedin_url?: string;
  };
}

export interface IPriceRate {
  status: boolean;
  data: {
    Rate_Name: string;
    Source_Amount: number;
    Target_Amount: number;
  }[];
}

export interface festival {
  festival_kind: FESTIVAL_KIND;
  festival_type: string;
  price_after_discount: number;
  price_on_festival: number;
  is_percent: boolean;
  discount_value: number;
  discount: number;
}

export enum TAG_EXPERIMENT {
  HOME_AGE_ORDER_ACTION = "home_age_order_action",
  CERTIFICATE_LINKEDIN_SHARE = "certificate_linkedin_share",
  SEARCH_AGA = "search_age2",
  NOWRUZ_REPORT_1404 = "nowrooz_report_1404",
  CERTIFICATE_LINKEDIN_SHARE_START = "certificate_linkedin_share_start",
  USERS_FEEDBACK_MAIN = "users_feedback_main",
  USERS_FEEDBACK_CONTACT = "users_feedback_contact",
  SUBSCRIPTION_PAGE = "subscription_page_v3",
  SUBSCRPTION_RECOMMENDER = "recommender_subscription",
  FARADARS_FOR_ORGANIZATIONS_FORM = "faradars-for-organizations-form",
}

export interface OrganizationFormExperiment extends logExperiment {
  tag: TAG_EXPERIMENT.FARADARS_FOR_ORGANIZATIONS_FORM;
  anyJoson: {
    name: string;
    organization: string;
    side: string;
    size: string;
    phone: string;
    email: string;
    address: string;
    description: string;
    url: string;
  };
}

export interface IGlobalSetting {
  data: {
    captcha: boolean;
    login: {
      enabled: boolean;
      message: string | null;
    };
    register: {
      enabled: boolean;
      message: string | null;
    };
    otp: {
      length: number;
      lifetime_seconds: number;
    };
    checkout: {
      enabled: boolean;
      message: string | null;
    };
  };
  status: boolean;
}

export enum CERTIFICATE_TYPE_LOG {
  POST = "post",
  DIRECT_ADD_CERTIFICATE_PROFILE = "direct_add_certificate_profile",
  ADD_CERTIFICATE_PROFILE = "add_certificate_profile",
}

export enum EXPIRY_STATUS {
  EXPIRABLE = "expirable",
  RENEWABLE = "renewable",
}

export enum ORDER_COUPON_TYPE {
  BEFORE = "before",
  AFTER = "after",
}
export enum FEEDBACK_TYPE {
  REPORT_PROBLEM = "report_problem",
  RECOMMEND_NEW_FEATURE = "recommend_new_feature",
}

interface feedbackExperiment extends logExperiment {
  anyJoson: {
    hash: string;
    type: FEEDBACK_TYPE;
    description: string;
    is_login: boolean;
    user_id: number | null;
    current_url: string;
    referrer_url: string;
    scroll_position: number | null;
    os_info: string | null;
    device_info: string | null;
    browser_info: string | null;
    screen_size: string | null;
  };
}

export interface FeedbackExperimentContact {
  tag: TAG_EXPERIMENT.USERS_FEEDBACK_CONTACT;
  anyJoson: {
    hash: string;
    mobile: string | null;
    email: string | null;
  };
}

interface subscriptionExperiment extends logExperiment {
  anyJoson: {
    is_login: boolean;
    user_id: number | null;
    referrer_url: string | null;
    action: SUBSCRIPTION_ACTION;
    plan_id: number | null;
    category_id: number | null;
    question_index: number | null;
  };
}

export enum SUBSCRIPTION_ACTION {
  SELECT_PLAN = "select_plan",
  ACTIVATE_PLAN = "activate_plan",
  APPROVE_PAY = "approve_pay",
  FILTER_CATEGORY = "filter_category",
  ACTIVATE_SUBSCRIPTION = "activate_subscription",
  EXPAND_FAQ = "expand_faq",
  LOGIN_REGISTER = "login_register",
  PRODUCT_PAGE_LAYOUT = "product_page_layout",
}

export enum SUBS_ACTION_TYPE {
  CLICK = "click",
  CLOSE = "close",
}
